import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { RiInformationLine } from "react-icons/ri";
import { getCreateDoseMedicationDefaults } from "@/components/dose/dose-form/create-dose-model";
import { DilutionPicker } from "@/components/dose/dose-form/dilution-picker";
import { DoseFormValues } from "@/components/dose/dose-form/dose-form-schema";
import { DoseComponentInventoryEntry } from "@/components/dose/dose-form/use-dose-component-inventory";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MedicationNameSelect } from "@/components/ui/medication-name-select";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue } from "@/components/ui/select";
import Tooltip from "@/components/ui/tooltip";
import { getDefaultDilution, getDilutionConcentration } from "@/lib/dilution";
import { formatQuantity, formatUnit } from "@/lib/format-data";
import { pickMedicationByName } from "@/lib/medication-select";
import { cx } from "@/lib/tailwind-utils";
import { Unit } from "@/types/enums/unit";
import { ORAL_MEDICATION_NAMES } from "@/types/enums/weight-loss-medications";
import { Medication } from "@/types/medication";
import { DoseLotPicker } from "./dose-lot-picker";
import { doseAllocationDecisionKey, doseAllocationError } from "./dose-lot-resolution";
import { proportionalDosePlan } from "./dose-stock-planning";
function LabelHint(t0) {
  const $ = _c(14);
  const {
    htmlFor,
    label,
    hint
  } = t0;
  let t1;
  if ($[0] !== htmlFor || $[1] !== label) {
    t1 = <Label htmlFor={htmlFor}>{label}</Label>;
    $[0] = htmlFor;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== label) {
    t2 = label.toLowerCase();
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `About ${t2}`;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <RiInformationLine className="size-3.5" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <button type="button" aria-label={t3} className="text-muted-foreground hover:text-foreground">{t4}</button>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== hint || $[9] !== t5) {
    t6 = <Tooltip content={hint} triggerAsChild={true}>{t5}</Tooltip>;
    $[8] = hint;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t6) {
    t7 = <div className="flex items-center gap-1">{t1}{t6}</div>;
    $[11] = t1;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
interface DoseComponentRowProps {
  index: number;
  inventory: DoseComponentInventoryEntry;
  medications: Medication[];
  onMedicationChange?: (index: number, name: string) => void;
}
export default function DoseComponentRow(t0) {
  const $ = _c(152);
  const {
    index,
    inventory,
    medications,
    onMedicationChange
  } = t0;
  const {
    control,
    register,
    setValue,
    clearErrors,
    formState: t1
  } = useFormContext();
  const {
    errors
  } = t1;
  const componentPath = `components.${index}` as const;
  register(`${componentPath}.typed.amount`);
  register(`${componentPath}.typed.quantity`);
  register(`${componentPath}.dilutionId`);
  register(`${componentPath}.lotAllocations`);
  register(`${componentPath}.singleLotSelection`);
  register(`${componentPath}.lotAllocationBasis`);
  register(`${componentPath}.stockAdministrationQuotas`);
  register(`${componentPath}.checkoutReuse`);
  register(`${componentPath}.correctActualInventoryUse`);
  register(`${componentPath}.inventoryDecisionKey`);
  register(`${componentPath}.inventoryPlanError`);
  const t2 = `${componentPath}.amount`;
  let t3;
  if ($[0] !== componentPath || $[1] !== setValue) {
    t3 = () => setValue(`${componentPath}.typed.amount`, true, {
      shouldDirty: true
    });
    $[0] = componentPath;
    $[1] = setValue;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== register || $[4] !== t2 || $[5] !== t3) {
    t4 = register(t2, {
      valueAsNumber: true,
      onChange: t3
    });
    $[3] = register;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const amountField = t4;
  const t5 = `${componentPath}.quantity`;
  let t6;
  if ($[7] !== componentPath || $[8] !== setValue) {
    t6 = () => setValue(`${componentPath}.typed.quantity`, true, {
      shouldDirty: true
    });
    $[7] = componentPath;
    $[8] = setValue;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== register || $[11] !== t5 || $[12] !== t6) {
    t7 = register(t5, {
      valueAsNumber: true,
      onChange: t6
    });
    $[10] = register;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const quantityField = t7;
  const t8 = `${componentPath}.name`;
  let t9;
  if ($[14] !== control || $[15] !== t8) {
    t9 = {
      control,
      name: t8
    };
    $[14] = control;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const selectedName = useWatch(t9);
  const t10 = `${componentPath}.unit`;
  let t11;
  if ($[17] !== control || $[18] !== t10) {
    t11 = {
      control,
      name: t10
    };
    $[17] = control;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const selectedUnit = useWatch(t11);
  const t12 = `${componentPath}.id`;
  let t13;
  if ($[20] !== control || $[21] !== t12) {
    t13 = {
      control,
      name: t12
    };
    $[20] = control;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const componentId = useWatch(t13);
  const t14 = `${componentPath}.medicationId`;
  let t15;
  if ($[23] !== control || $[24] !== t14) {
    t15 = {
      control,
      name: t14
    };
    $[23] = control;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const medicationId = useWatch(t15);
  const amount = Number(useWatch({
    control,
    name: `${componentPath}.amount`
  }) ?? 0);
  const quantity = Number(useWatch({
    control,
    name: `${componentPath}.quantity`
  }) ?? 1);
  const t16 = `${componentPath}.dilutionId`;
  let t17;
  if ($[26] !== control || $[27] !== t16) {
    t17 = {
      control,
      name: t16
    };
    $[26] = control;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const dilutionId = useWatch(t17);
  let t18;
  if ($[29] !== componentPath || $[30] !== control) {
    t18 = {
      control,
      name: componentPath
    };
    $[29] = componentPath;
    $[30] = control;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const componentValues = useWatch(t18);
  let t19;
  if ($[32] !== componentValues || $[33] !== inventory) {
    t19 = proportionalDosePlan(componentValues, inventory);
    $[32] = componentValues;
    $[33] = inventory;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const stagedPlan = t19;
  let t20;
  let t21;
  if ($[35] !== componentPath || $[36] !== componentValues || $[37] !== setValue || $[38] !== stagedPlan) {
    t20 = () => {
      if (!stagedPlan) {
        return;
      }
      for (const field of ["lotAllocationBasis", "lotAllocations", "stockAdministrationQuotas"] as const) {
        if (JSON.stringify(componentValues[field]) !== JSON.stringify(stagedPlan[field])) {
          setValue(`${componentPath}.${field}`, stagedPlan[field], {
            shouldDirty: true,
            shouldValidate: true
          });
        }
      }
    };
    t21 = [stagedPlan, componentValues, componentPath, setValue];
    $[35] = componentPath;
    $[36] = componentValues;
    $[37] = setValue;
    $[38] = stagedPlan;
    $[39] = t20;
    $[40] = t21;
  } else {
    t20 = $[39];
    t21 = $[40];
  }
  useEffect(t20, t21);
  let t22;
  if ($[41] !== componentValues || $[42] !== inventory) {
    t22 = doseAllocationError(componentValues, inventory);
    $[41] = componentValues;
    $[42] = inventory;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const planError = t22;
  let t23;
  if ($[44] !== componentValues || $[45] !== inventory) {
    t23 = doseAllocationDecisionKey(componentValues, inventory);
    $[44] = componentValues;
    $[45] = inventory;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const decisionKey = t23;
  let t24;
  let t25;
  if ($[47] !== componentPath || $[48] !== decisionKey || $[49] !== setValue) {
    t24 = () => {
      setValue(`${componentPath}.inventoryDecisionKey`, decisionKey, {
        shouldValidate: true
      });
    };
    t25 = [decisionKey, componentPath, setValue];
    $[47] = componentPath;
    $[48] = decisionKey;
    $[49] = setValue;
    $[50] = t24;
    $[51] = t25;
  } else {
    t24 = $[50];
    t25 = $[51];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[52] !== clearErrors || $[53] !== componentPath || $[54] !== planError || $[55] !== setValue) {
    t26 = () => {
      setValue(`${componentPath}.inventoryPlanError`, planError, {
        shouldValidate: true
      });
      if (!planError) {
        clearErrors(`${componentPath}.lotSelection`);
      }
    };
    t27 = [planError, componentPath, setValue, clearErrors];
    $[52] = clearErrors;
    $[53] = componentPath;
    $[54] = planError;
    $[55] = setValue;
    $[56] = t26;
    $[57] = t27;
  } else {
    t26 = $[56];
    t27 = $[57];
  }
  useEffect(t26, t27);
  const componentErrors = errors.components?.[index];
  const isExistingComponent = !!componentId;
  let t28;
  if ($[58] !== medications || $[59] !== selectedName) {
    t28 = selectedName ? Array.from(new Set(medications.filter(medication => medication.name === selectedName && medication.unit).map(_temp))) : [];
    $[58] = medications;
    $[59] = selectedName;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  const configuredUnits = t28;
  let t29;
  if ($[61] !== medications || $[62] !== selectedName) {
    t29 = !!selectedName && medications.some(medication_1 => medication_1.name === selectedName);
    $[61] = medications;
    $[62] = selectedName;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  const nameHasCatalogRow = t29;
  let t30;
  if ($[64] !== configuredUnits || $[65] !== nameHasCatalogRow) {
    t30 = configuredUnits.length > 0 ? configuredUnits : nameHasCatalogRow ? Object.values(Unit) as Unit[] : [];
    $[64] = configuredUnits;
    $[65] = nameHasCatalogRow;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  const catalogUnits = t30;
  let dilutions;
  let t31;
  if ($[67] !== dilutionId || $[68] !== medicationId || $[69] !== medications) {
    dilutions = medications.find(medication_2 => medication_2.id === medicationId)?.dilutions ?? [];
    let t32;
    if ($[72] !== dilutionId) {
      t32 = dilution => dilution.id === dilutionId;
      $[72] = dilutionId;
      $[73] = t32;
    } else {
      t32 = $[73];
    }
    t31 = dilutions.find(t32);
    $[67] = dilutionId;
    $[68] = medicationId;
    $[69] = medications;
    $[70] = dilutions;
    $[71] = t31;
  } else {
    dilutions = $[70];
    t31 = $[71];
  }
  const selectedDilution = t31;
  let t32;
  if ($[74] !== catalogUnits || $[75] !== selectedDilution?.vialStrength) {
    t32 = selectedDilution?.vialStrength && !catalogUnits.includes(Unit.ML) ? [...catalogUnits, Unit.ML] : catalogUnits;
    $[74] = catalogUnits;
    $[75] = selectedDilution?.vialStrength;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  const chartableUnits = t32;
  let t33;
  if ($[77] !== chartableUnits || $[78] !== isExistingComponent || $[79] !== selectedUnit) {
    t33 = isExistingComponent && selectedUnit && !chartableUnits.includes(selectedUnit as Unit) ? [...chartableUnits, selectedUnit as Unit] : chartableUnits;
    $[77] = chartableUnits;
    $[78] = isExistingComponent;
    $[79] = selectedUnit;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  const availableUnits = t33;
  let t34;
  let t35;
  if ($[81] !== availableUnits || $[82] !== componentPath || $[83] !== isExistingComponent || $[84] !== medications || $[85] !== selectedName || $[86] !== selectedUnit || $[87] !== setValue) {
    t34 = () => {
      if (isExistingComponent) {
        return;
      }
      if (availableUnits.length === 0) {
        return;
      }
      if (!availableUnits.includes(selectedUnit as Unit)) {
        const preferredUnit = selectedName ? getCreateDoseMedicationDefaults(selectedName, medications).component.unit : undefined;
        const nextUnit = preferredUnit && availableUnits.includes(preferredUnit) ? preferredUnit : availableUnits[0];
        setValue(`${componentPath}.unit`, nextUnit, {
          shouldValidate: true
        });
      }
    };
    t35 = [isExistingComponent, availableUnits, selectedUnit, selectedName, medications, componentPath, setValue];
    $[81] = availableUnits;
    $[82] = componentPath;
    $[83] = isExistingComponent;
    $[84] = medications;
    $[85] = selectedName;
    $[86] = selectedUnit;
    $[87] = setValue;
    $[88] = t34;
    $[89] = t35;
  } else {
    t34 = $[88];
    t35 = $[89];
  }
  useEffect(t34, t35);
  let t36;
  let t37;
  if ($[90] !== componentPath || $[91] !== medicationId || $[92] !== medications || $[93] !== selectedName || $[94] !== setValue) {
    t36 = () => {
      if (medicationId || !selectedName) {
        return;
      }
      const resolved = pickMedicationByName(medications, selectedName);
      if (resolved) {
        setValue(`${componentPath}.medicationId`, resolved.id, {
          shouldValidate: true
        });
      }
    };
    t37 = [medicationId, selectedName, medications, componentPath, setValue];
    $[90] = componentPath;
    $[91] = medicationId;
    $[92] = medications;
    $[93] = selectedName;
    $[94] = setValue;
    $[95] = t36;
    $[96] = t37;
  } else {
    t36 = $[95];
    t37 = $[96];
  }
  useEffect(t36, t37);
  const openedWithMedicationId = useRef(medicationId);
  let t38;
  let t39;
  if ($[97] !== componentPath || $[98] !== dilutionId || $[99] !== dilutions || $[100] !== isExistingComponent || $[101] !== medicationId || $[102] !== selectedDilution || $[103] !== setValue) {
    t38 = () => {
      if (!medicationId || selectedDilution) {
        return;
      }
      if (isExistingComponent && medicationId === openedWithMedicationId.current) {
        return;
      }
      const next = getDefaultDilution(dilutions)?.id ?? null;
      if (next !== (dilutionId ?? null)) {
        setValue(`${componentPath}.dilutionId`, next, {
          shouldDirty: true
        });
      }
    };
    t39 = [medicationId, selectedDilution, isExistingComponent, dilutions, dilutionId, componentPath, setValue];
    $[97] = componentPath;
    $[98] = dilutionId;
    $[99] = dilutions;
    $[100] = isExistingComponent;
    $[101] = medicationId;
    $[102] = selectedDilution;
    $[103] = setValue;
    $[104] = t38;
    $[105] = t39;
  } else {
    t38 = $[104];
    t39 = $[105];
  }
  useEffect(t38, t39);
  let t40;
  if ($[106] !== selectedDilution) {
    t40 = selectedDilution ? getDilutionConcentration(selectedDilution) : null;
    $[106] = selectedDilution;
    $[107] = t40;
  } else {
    t40 = $[107];
  }
  const concentration = t40;
  let t41;
  if ($[108] !== medicationId) {
    t41 = medication_3 => medication_3.id === medicationId;
    $[108] = medicationId;
    $[109] = t41;
  } else {
    t41 = $[109];
  }
  const selectedMedication = medications.find(t41);
  const medicationUnit = selectedMedication?.unit as Unit | undefined;
  const showQuantity = selectedMedication?.showDoseQuantity !== false || isExistingComponent && quantity !== 1;
  let t42;
  let t43;
  if ($[110] !== componentPath || $[111] !== isExistingComponent || $[112] !== quantity || $[113] !== setValue || $[114] !== showQuantity) {
    t42 = () => {
      if (showQuantity || isExistingComponent || quantity === 1) {
        return;
      }
      setValue(`${componentPath}.quantity`, 1, {
        shouldValidate: true
      });
    };
    t43 = [showQuantity, isExistingComponent, quantity, componentPath, setValue];
    $[110] = componentPath;
    $[111] = isExistingComponent;
    $[112] = quantity;
    $[113] = setValue;
    $[114] = showQuantity;
    $[115] = t42;
    $[116] = t43;
  } else {
    t42 = $[115];
    t43 = $[116];
  }
  useEffect(t42, t43);
  const dilutionHint = concentration && amount > 0 ? selectedUnit === Unit.ML ? `≈ ${formatQuantity(Number((amount * concentration).toFixed(2)))} ${formatUnit(medicationUnit ?? Unit.UNITS)}` : selectedUnit === medicationUnit ? `≈ ${formatQuantity(Number((amount / concentration).toFixed(3)))} mL drawn up` : null : null;
  let t44;
  if ($[117] !== selectedName) {
    t44 = ORAL_MEDICATION_NAMES.includes(selectedName);
    $[117] = selectedName;
    $[118] = t44;
  } else {
    t44 = $[118];
  }
  const isOral = t44;
  const t45 = `${componentPath}.name`;
  const t46 = componentErrors?.name;
  let t47;
  if ($[119] !== componentPath || $[120] !== index || $[121] !== medications || $[122] !== onMedicationChange || $[123] !== setValue) {
    t47 = name => {
      setValue(`${componentPath}.includeDepletedMedicationId`, undefined);
      setValue(`${componentPath}.lotAllocations`, undefined);
      setValue(`${componentPath}.singleLotSelection`, undefined);
      setValue(`${componentPath}.lotAllocationBasis`, undefined);
      setValue(`${componentPath}.clinicalShipmentIds`, undefined);
      setValue(`${componentPath}.stockAdministrationQuotas`, undefined);
      setValue(`${componentPath}.checkoutReuse`, undefined);
      setValue(`${componentPath}.correctActualInventoryUse`, undefined);
      setValue(`${componentPath}.lotSelection`, null, {
        shouldDirty: true
      });
      setValue(`${componentPath}.medicationId`, pickMedicationByName(medications, name)?.id, {
        shouldDirty: true,
        shouldValidate: true
      });
      onMedicationChange?.(index, name);
    };
    $[119] = componentPath;
    $[120] = index;
    $[121] = medications;
    $[122] = onMedicationChange;
    $[123] = setValue;
    $[124] = t47;
  } else {
    t47 = $[124];
  }
  let t48;
  if ($[125] !== control || $[126] !== t45 || $[127] !== t46 || $[128] !== t47) {
    t48 = <MedicationNameSelect name={t45} control={control} error={t46} required={true} onValueChange={t47} />;
    $[125] = control;
    $[126] = t45;
    $[127] = t46;
    $[128] = t47;
    $[129] = t48;
  } else {
    t48 = $[129];
  }
  let t49;
  if ($[130] !== amountField || $[131] !== availableUnits || $[132] !== componentErrors?.amount || $[133] !== componentErrors?.quantity || $[134] !== componentErrors?.unit || $[135] !== componentPath || $[136] !== control || $[137] !== dilutionHint || $[138] !== dilutionId || $[139] !== dilutions || $[140] !== index || $[141] !== inventory || $[142] !== isOral || $[143] !== medicationUnit || $[144] !== quantityField || $[145] !== selectedName || $[146] !== setValue || $[147] !== showQuantity) {
    t49 = !!selectedName?.trim() && <>{dilutions.length > 0 && <div className="space-y-1"><Label htmlFor={`${componentPath}.dilution`}>Reconstitution</Label><DilutionPicker id={`${componentPath}.dilution`} dilutions={dilutions} value={dilutionId} unit={medicationUnit} onChange={next_0 => setValue(`${componentPath}.dilutionId`, next_0, {
          shouldDirty: true
        })} /></div>}<div className={cx("grid gap-4", showQuantity ? "grid-cols-3" : "grid-cols-2")}><div className="space-y-1"><LabelHint htmlFor={`${componentPath}.amount`} label="Amount" hint="How much is given in one administration, such as 20 units or 0.5 mL." /><Input id={`${componentPath}.amount`} type="number" step="0.001" stripLeadingZeros={false} className="w-full" placeholder="Enter amount" aria-invalid={!!componentErrors?.amount} hasError={!!componentErrors?.amount} errorMessage={componentErrors?.amount?.message} {...amountField} />{dilutionHint && !componentErrors?.amount && <p className="text-caption text-muted-foreground tabular-nums">{dilutionHint}</p>}</div><div className="space-y-1"><Label htmlFor={`${componentPath}.unit`}>Unit</Label><Controller name={`${componentPath}.unit`} control={control} render={t50 => {
            const {
              field: field_0
            } = t50;
            return <SearchSelect value={field_0.value} onValueChange={field_0.onChange} disabled={!selectedName}><SearchSelectTrigger hasError={!!componentErrors?.unit}><SearchSelectValue placeholder={selectedName ? "Select unit" : "Select medication first"} formatValue={_temp2} /></SearchSelectTrigger><SearchSelectContent searchPlaceholder="Search units..." emptyMessage="No units configured for this medication.">{availableUnits.map(_temp3)}</SearchSelectContent></SearchSelect>;
          }} /></div><div className={cx("space-y-1", !showQuantity && "hidden")}><LabelHint htmlFor={`${componentPath}.quantity`} label="Quantity" hint="How many times the amount is given. 2 syringes of 1 mL is an amount of 1 mL and a quantity of 2." /><Input id={`${componentPath}.quantity`} type="number" step="0.1" className="w-full" placeholder="Enter quantity" aria-invalid={!!componentErrors?.quantity} hasError={!!componentErrors?.quantity} errorMessage={componentErrors?.quantity?.message} {...quantityField} /></div></div><div className={isOral ? "hidden" : "space-y-2"}><DoseLotPicker index={index} inventory={inventory} /></div></>;
    $[130] = amountField;
    $[131] = availableUnits;
    $[132] = componentErrors?.amount;
    $[133] = componentErrors?.quantity;
    $[134] = componentErrors?.unit;
    $[135] = componentPath;
    $[136] = control;
    $[137] = dilutionHint;
    $[138] = dilutionId;
    $[139] = dilutions;
    $[140] = index;
    $[141] = inventory;
    $[142] = isOral;
    $[143] = medicationUnit;
    $[144] = quantityField;
    $[145] = selectedName;
    $[146] = setValue;
    $[147] = showQuantity;
    $[148] = t49;
  } else {
    t49 = $[148];
  }
  let t50;
  if ($[149] !== t48 || $[150] !== t49) {
    t50 = <div className="space-y-3">{t48}{t49}</div>;
    $[149] = t48;
    $[150] = t49;
    $[151] = t50;
  } else {
    t50 = $[151];
  }
  return t50;
}
function _temp3(unit) {
  return <SearchSelectItem key={unit} value={unit}>{formatUnit(unit)}</SearchSelectItem>;
}
function _temp2(value) {
  return formatUnit(value as Unit);
}
function _temp(medication_0) {
  return medication_0.unit as Unit;
}